import React, { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import styles from './HealthPlanBox.module.scss';
import Text from '../../Base/Text/Text';
import { HealthPlanTimelineItem } from '../../../Models/HealthPlan/HealthPlanTimelineItem';
import { joinClasses } from '../../../Utils/className.util';
import { useAppContext } from '../../../Services/AppContext';
import NetteApiClient from '../../../Services/NetteApiClient';
import AppointmentModal from '../Appointment/AppointmentModal';
import RemoveButton from '../../Button/RemoveButton';
import apiClient from '../../../Services/ApiClient';
import { HEALTH_PLAN_USER } from '../../../Modules/HealthPlan/healthPlanUser.graphlql';
import HealthPlanOtherConsultationsModal from '../../../Modules/HealthPlan/Components/HealthPlanOtherConsultations/HealthPlanOtherConsultationsModal';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import HealthPlanLaboratoryExamModal from '../../../Modules/HealthPlan/Components/HealthPlanLaboratoryExam/HealthPlanLaboratoryExamModal';
import HealthPlanEducationMaterialsModal from '../../../Modules/HealthPlan/Components/HealthPlanEducationMaterials/HealthPlanEducationMaterialsModal';
import HealthPlanMonitoringModal from '../../../Modules/HealthPlan/Components/HealthPlanMonitoring/HealthPlanMonitoringModal';
import HealthPlanDiagnosticImagingModal from '../../../Modules/HealthPlan/Components/HealthPlanDiagnosticImaging/HealthPlanDiagnosticImagingModal';
import HealthPlanVaccinationModal from '../../../Modules/HealthPlan/Components/HealthPlanVaccination/HealthPlanVaccinationModal';
import HealthPlanElectronicPrescriptionModal from '../../../Modules/HealthPlan/Components/HealthPlanElectronicPrescription/HealthPlanElectronicPrescriptionModal';
import HealthPlanMedicalReportModal from '../../../Modules/HealthPlan/Components/HealthPlanMedicalReport/HealthPlanMedicalReportModal';
import { ItemType } from '../../../Enums/ItemType.enum';
import ConfirmationModal from '../../../Modules/Base/Components/Confirmation/ConfirmationModal';
import { Theme } from '../../../Enums/Theme.enum';
import Button from '../../Button/Button';
import useTranslation from '../../../Hooks/useTranslation';
import MediaViewerModal from '../../MediaViewer/MediaViewerModal';
import EcardFileViewerButton from '../../../Modules/Ecard/Components/EcardFileViewer/EcardFileViewerButton';

interface HealthPlanBox {
	item: HealthPlanTimelineItem;
	patientId: string;
	workplaceId: string;
	reloadData?: any;
}

export default function HealthPlanBox(props: HealthPlanBox) {
	const { item, patientId, workplaceId, reloadData } = props;
	const { title, className, type } = item;
	const { status, custom, doctorStatus } = item.extra;
	let { hintText, statusText } = item.extra.data;
	const { openItemModal, translation, links } = useAppContext();
	const [disabled, setDisabled] = useState(false);
	const { healthPlan, setActiveHealthPlanUserItem } = useHealthPlanContext();
	const [currentStatus, setCurrentStatus] = useState(doctorStatus ?? status);
	const [informationModalIsOpen, setInformationModalIsOpen] = useState(false);
	const { t } = useTranslation();
	const [isMediaViewerOpened, setIsMediaViewerOpened] = useState(false);

	const [modalOpened, setModalOpened] = useState({
		appointment: false,
		medicalEvaluation: false,
		otherConsultation: false,
		laboratoryExamination: false,
		educationMaterial: false,
		monitoring: false,
		diagnosticImaging: false,
		vaccination: false,
		electronicPrescription: false,
	});

	useEffect(() => {
		setDisabled(status === 'cantFillNow');
	}, [currentStatus, disabled]);

	useEffect(() => {
		setCurrentStatus(doctorStatus ?? status);
	}, [doctorStatus, status]);

	const handleOpenItemModal = () => {
		setInformationModalIsOpen(false);
		setActiveHealthPlanUserItem(item);
		switch (item.type) {
			case ItemType.APPOINTMENT:
			case ItemType.MEDICAL_EVALUATION:
			case ItemType.OTHER_CONSULTATION:
			case ItemType.LABORATORY_EXAMINATION:
			case ItemType.EDUCATION_MATERIAL:
			case ItemType.MONITORING:
			case ItemType.DIAGNOSTIC_IMAGING:
			case ItemType.VACCINATION:
			case ItemType.ELECTRONIC_PRESCRIPTION:
				handleOpenModal(item.type);
				break;
			default:
				NetteApiClient.get(openItemModal.replace('__ID__', item.id)).catch(
					console.error,
				);
				break;
		}
	};

	const handleClick = (): void => {
		if (item.extra?.isLocked) {
			setInformationModalIsOpen(true);
		} else {
			handleOpenItemModal();
		}
	};

	let icon;

	switch (currentStatus) {
		case 'haveToFillNow':
			statusText =
				statusText ?? translation['component.healthPlan.label.youHaveToFill'];
			hintText =
				hintText ?? translation['component.healthPlan.action.clickToFill'];
			icon = 'circle-plus-solid.svg';
			break;
		case 'scheduled':
		case 'prescheduled':
			statusText =
				statusText ?? translation['component.healthPlan.label.planned'];
			hintText =
				hintText ?? translation['component.healthPlan.action.clickToCustomize'];
			icon = 'checked.svg';
			break;
		case 'cantFillNow':
			statusText =
				statusText ??
				translation['component.healthPlan.label.requiredInFuture'];
			hintText =
				hintText ?? translation['component.healthPlan.action.clickToCustomize'];
			break;
		case 'canFillNow':
			statusText =
				statusText ?? translation['component.healthPlan.label.optional'];
			hintText =
				hintText ?? translation['component.healthPlan.action.clickToFill'];
			icon = 'circle-plus-solid.svg';
			break;
		case 'completed':
			statusText =
				statusText ?? translation['component.healthPlan.label.completed'];
			hintText =
				hintText ?? translation['component.healthPlan.action.clickToShow'];
			icon = 'checked.svg';
			break;
	}

	const handleOpenModal = (key: string) => {
		setModalOpened(prevValue => {
			return {
				...prevValue,
				[key]: true,
			};
		});
	};

	const handleCloseModal = (key: string) => {
		setModalOpened(prevValue => {
			return {
				...prevValue,
				[key]: false,
			};
		});

		reloadData?.();
	};

	const handleOnRemoveExtra = async (): Promise<void> => {
		const {
			data: { healthPlanUser },
		} = await apiClient.graphql({
			query: HEALTH_PLAN_USER,
			variables: {
				userId: patientId,
				healthPlanCode: healthPlan?.code,
				workplaceId: workplaceId,
			},
			fetchPolicy: 'network-only',
		});

		if (healthPlanUser) {
			await apiClient.delete(
				'/health-plan/' +
					healthPlanUser?.healthPlanId +
					'/user/' +
					healthPlanUser.id +
					'/item/' +
					item.id,
			);
		}

		reloadData?.();
	};

	const confirmationModalText = useMemo(() => {
		let text =
			t('component.healthPlanBox.infoMessage', {
				title: title,
			}) ?? '';

		if (item.type === ItemType.APPOINTMENT) {
			text = t('component.healthPlanBox.infoMessageForAppointment') ?? '';
		}

		return text;
	}, [item]);

	const confirmationModalButton = useMemo(() => {
		return item.type !== ItemType.ELECTRONIC_PRESCRIPTION;
	}, [item]);

	const electronicPrescriptionButton = useMemo(() => {
		return item.type === ItemType.ELECTRONIC_PRESCRIPTION;
	}, [item]);

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					styles[currentStatus],
					custom && styles.extra,
					disabled && styles.disabled,
					className,
				])}
				onClick={() => !disabled && !custom && handleClick()}>
				<div
					className={joinClasses([
						'd-flex',
						'flex-row',
						custom ? undefined : 'justify-content-between',
					])}>
					<div
						className={'flex-grow-1'}
						onClick={() => custom && handleClick()}>
						{!!statusText && <Text tx={statusText} className={styles.status} />}
						{!!title && <Text tx={title} className={styles.title} />}
						{!!hintText && <Text tx={hintText} className={styles.hint} />}
					</div>
					<div>
						{!custom ? (
							<div className='pt-4'>
								{!!icon && <img src={'/dist/icons/react/' + icon} />}
							</div>
						) : (
							<div
								className={'d-flex flex-column justify-content-between h-100'}>
								<div className={styles.extraBadge}>
									{translation['base.extra']}
								</div>
								{item.extra?.status !== 'completed' && (
									<div className={styles.extraDelete}>
										<RemoveButton
											onClick={handleOnRemoveExtra}
											title={'base.shared.remove'}
										/>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
			{item.type === ItemType.APPOINTMENT && (
				<AppointmentModal
					opened={modalOpened.appointment}
					onClose={() => handleCloseModal(item.type)}
					patientId={patientId}
					healthPlanUserItemId={item.id}
					workplaceId={workplaceId}
					allowPickMeetingType={true}
					eventId={item.extra?.eventId}
					onEventIsCreated={() => {
						handleCloseModal(item.type);
					}}
				/>
			)}
			{item.type === ItemType.MEDICAL_EVALUATION && (
				<HealthPlanMedicalReportModal
					patientId={patientId}
					workplaceId={workplaceId}
					opened={modalOpened.medicalEvaluation}
					disableSubmit={item.extra?.isLocked}
					onClose={() => handleCloseModal(item.type)}
				/>
			)}
			{item.type === ItemType.OTHER_CONSULTATION && (
				<HealthPlanOtherConsultationsModal
					opened={modalOpened.otherConsultation}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
				/>
			)}
			{item.type === ItemType.LABORATORY_EXAMINATION && (
				<HealthPlanLaboratoryExamModal
					opened={modalOpened.laboratoryExamination}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
				/>
			)}
			{item.type === ItemType.EDUCATION_MATERIAL && (
				<HealthPlanEducationMaterialsModal
					opened={modalOpened.educationMaterial}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
					patientId={patientId}
				/>
			)}
			{item.type === ItemType.MONITORING && (
				<HealthPlanMonitoringModal
					opened={modalOpened.monitoring}
					onClose={() => handleCloseModal(item.type)}
				/>
			)}
			{item.type === ItemType.DIAGNOSTIC_IMAGING && (
				<HealthPlanDiagnosticImagingModal
					opened={modalOpened.diagnosticImaging}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
				/>
			)}
			{item.type === ItemType.VACCINATION && (
				<HealthPlanVaccinationModal
					opened={modalOpened.vaccination}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
				/>
			)}
			{item.type === ItemType.ELECTRONIC_PRESCRIPTION && (
				<HealthPlanElectronicPrescriptionModal
					opened={modalOpened.electronicPrescription}
					onClose={() => handleCloseModal(item.type)}
					disableSubmit={item.extra?.isLocked}
					patientId={patientId}
					defaultValues={item.extra?.data}
				/>
			)}
			<ConfirmationModal
				title={'base.shared.notice'}
				text={confirmationModalText}
				isOpen={informationModalIsOpen}
				onClose={() => setInformationModalIsOpen(false)}>
				{confirmationModalButton ? (
					<Button
						title={'component.healthPlanBox.understand'}
						theme={Theme.GREEN}
						onClick={handleOpenItemModal}
					/>
				) : (
					<></>
				)}
				{electronicPrescriptionButton && item?.extra?.prescription?.id && (
					<>
						<Button
							title={'base.ePrescription'}
							theme={Theme.GREEN}
							onClick={() => setIsMediaViewerOpened(true)}
						/>
						<MediaViewerModal
							opened={isMediaViewerOpened}
							onClose={() => setIsMediaViewerOpened(false)}
							file={{
								id: item?.extra?.prescription?.id,
								displayName: t('base.ePrescription') ?? '',
								type: 'pdf',
								preview: links.treatment.downloadPrescription
									.replace('__PATIENT__', patientId)
									.replace('__ID__', item?.extra?.prescription?.id),
								download: links.treatment.downloadPrescription
									.replace('__PATIENT__', patientId)
									.replace('__ID__', item?.extra?.prescription?.id),
							}}
						/>
					</>
				)}
			</ConfirmationModal>
		</>
	);
}
